import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import {
  Triangle,
  Loader2,
  CloudUpload,
  Image as ImageIcon
} from 'lucide-react';
import {
  sanitizeAndFormatHtml,
  HERO_ICON_OPTIONS,
  normalizeHeroIconName,
  getYouTubeVideoId,
  resolveVideoThumbnail,
  extractMapEmbedUrl
} from '../../components/BranchLocator/BranchPageTemplate';

export const cleanAndNormalizeSchema = (input) => {
  if (!input) return null;
  if (typeof input === 'object') return input;
  let str = String(input).trim();
  if (!str) return null;

  // 1. Remove wrapping <script ...> and </script> tags if present
  str = str.replace(/^<script[^>]*>/i, '').replace(/<\/script>$/i, '').trim();

  // 2. If it contains JS variable declaration (e.g. const branchLocatorSchema = { ... }; or // comments)
  // Extract content between the first { or [ and the last } or ]
  const firstBrace = str.search(/[{[]/);
  const lastBrace = Math.max(str.lastIndexOf('}'), str.lastIndexOf(']'));
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    str = str.slice(firstBrace, lastBrace + 1);
  }

  // 3. Try standard JSON.parse first
  try {
    return JSON.parse(str);
  } catch (e1) {
    // 4. Try evaluating JS object literal (supports unquoted keys, single quotes, trailing commas)
    try {
      // eslint-disable-next-line no-new-func
      const parsed = Function('"use strict"; return (' + str + ');')();
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (e2) {
      // Return as cleaned string if parsing fails
      return str;
    }
  }
  return str;
};

const SECTION_LABELS = {
  hero: 'Hero Section',
  features_strip: 'Features Strip',
  youtube_videos: 'YouTube Videos',
  why_choose: 'Why Choose',
  local_insights: 'Local Insights',
  loan_products: 'Loan Products',
  how_to_apply: 'How To Apply',
  eligibility: 'Eligibility',
  customer_stories: 'Customer Stories',
  visit_us: 'Visit Us / Map',
  gallery: 'Gallery',
  coverage: 'Coverage Areas',
  faq: 'FAQ Section',
  cta: 'CTA Section',
  seo: 'SEO Settings'
};

const AdminGMBSectionEditor = () => {
  const { branchId, sectionKey } = useParams();
  const navigate = useNavigate();
  const [sectionData, setSectionData] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch branch info
      const branchRes = await axios.get('/api/gmb-branches');
      if (branchRes.data.success) {
        const found = branchRes.data.data.find(b => b.id === parseInt(branchId));
        setBranchInfo(found);
      }

      // Fetch section data
      const sectionRes = await axios.get(`/api/gmb-branches/${branchId}/sections/${sectionKey}`);
      if (sectionRes.data.success) {
        let data = sectionRes.data.data.section_data;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (e) { /* keep as-is */ }
        }
        setSectionData(data);
      }
    } catch (error) {
      console.error('Error fetching section:', error);
      if (error.response?.status === 404) {
        setSectionData({});
      } else {
        toast.error('Failed to load section data');
      }
    } finally {
      setLoading(false);
    }
  }, [branchId, sectionKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      let payload = sectionData;
      if (payload) {
        payload = {
          ...payload,
          ...(payload.description !== undefined ? { description: sanitizeAndFormatHtml(payload.description) } : {}),
          ...(payload.description2 !== undefined ? { description2: sanitizeAndFormatHtml(payload.description2) } : {}),
          ...(Array.isArray(payload.cards) ? {
            cards: payload.cards.map(c => ({
              ...c,
              ...(c.description !== undefined ? { description: sanitizeAndFormatHtml(c.description) } : {})
            }))
          } : {}),
          ...(Array.isArray(payload.products) ? {
            products: payload.products.map(p => ({
              ...p,
              ...(p.description !== undefined ? { description: sanitizeAndFormatHtml(p.description) } : {})
            }))
          } : {}),
          ...(Array.isArray(payload.steps) ? {
            steps: payload.steps.map(s => ({
              ...s,
              ...(s.description !== undefined ? { description: sanitizeAndFormatHtml(s.description) } : {})
            }))
          } : {}),
          ...(Array.isArray(payload.reviews) ? {
            reviews: payload.reviews.map(r => ({
              ...r,
              ...(r.text !== undefined ? { text: sanitizeAndFormatHtml(r.text) } : {})
            }))
          } : {}),
          ...(Array.isArray(payload.videos) ? {
            videos: payload.videos.map(v => {
              const isYt = (url) => typeof url === 'string' && (url.includes('youtube.com') || url.includes('youtu.be'));
              const videoUrl = v.video_url || (isYt(v.img) ? v.img : '');
              let img = v.img;
              if (isYt(img)) {
                const vidId = getYouTubeVideoId(img);
                img = vidId ? `https://img.youtube.com/vi/${vidId}/hqdefault.jpg` : '';
              } else if (!img && videoUrl) {
                const vidId = getYouTubeVideoId(videoUrl);
                if (vidId) img = `https://img.youtube.com/vi/${vidId}/hqdefault.jpg`;
              }
              return {
                ...v,
                title: v.title || '',
                video_url: videoUrl || '',
                img: img || ''
              };
            })
          } : {}),
          ...(sectionKey === 'seo' && payload.schema !== undefined ? {
            schema: cleanAndNormalizeSchema(payload.schema)
          } : {})
        };
      }
      await axios.put(`/api/gmb-branches/${branchId}/sections/${sectionKey}`, {
        section_data: payload
      });
      if (payload !== sectionData) {
        setSectionData(payload);
      }
      toast.success('Section saved successfully!');
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setSectionData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[key];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      obj[lastKey] = value;
      return copy;
    });
  };

  const addArrayItem = (path, template) => {
    setSectionData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (const key of keys) {
        obj = obj[isNaN(key) ? key : parseInt(key)];
      }
      obj.push(template);
      return copy;
    });
  };

  const removeArrayItem = (path, index) => {
    setSectionData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (const key of keys) {
        obj = obj[isNaN(key) ? key : parseInt(key)];
      }
      obj.splice(index, 1);
      return copy;
    });
  };

  const moveArrayItem = (path, fromIndex, direction) => {
    setSectionData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (const key of keys) {
        obj = obj[isNaN(key) ? key : parseInt(key)];
      }
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= obj.length) return prev;
      [obj[fromIndex], obj[toIndex]] = [obj[toIndex], obj[fromIndex]];
      return copy;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!sectionData) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Section data not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-indigo-600 hover:underline">Go Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/gmb-branch-pages')} className="p-2 bg-white rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <p className="text-sm text-indigo-600 font-medium">{branchInfo?.branch_name || 'Branch'} Branch</p>
          <h1 className="text-2xl font-bold text-slate-800">{SECTION_LABELS[sectionKey] || sectionKey}</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 shadow-lg shadow-indigo-200"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Dynamic Form */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        {renderSectionForm(sectionKey, sectionData, updateField, addArrayItem, removeArrayItem, moveArrayItem, branchInfo)}
      </div>
    </div>
  );
};

// ============================================
// SECTION-SPECIFIC FORM RENDERERS
// ============================================

function renderSectionForm(sectionKey, data, updateField, addArrayItem, removeArrayItem, moveArrayItem, branchInfo) {
  switch (sectionKey) {
    case 'hero': return <HeroForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} />;
    case 'features_strip': return <FeaturesStripForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'youtube_videos': return <YouTubeForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'why_choose': return <CardsForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} cardFields={['number', 'title', 'description']} arrayKey="cards" />;
    case 'local_insights': return <CardsForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} cardFields={['title', 'description', 'icon']} arrayKey="cards" />;
    case 'loan_products': return <LoanProductsForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'how_to_apply': return <StepsForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'eligibility': return <CardsForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} cardFields={['title', 'description']} arrayKey="cards" />;
    case 'customer_stories': return <ReviewsForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'visit_us': return <VisitUsForm data={data} update={updateField} />;
    case 'gallery': return <GalleryForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} />;
    case 'coverage': return <CoverageForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'faq': return <FAQForm data={data} update={updateField} add={addArrayItem} remove={removeArrayItem} move={moveArrayItem} />;
    case 'cta': return <CTAForm data={data} update={updateField} />;
    case 'seo': return <SEOForm data={data} update={updateField} branchInfo={branchInfo} />;
    default: return <JSONEditor data={data} update={updateField} />;
  }
}

// ============================================
// REUSABLE FORM COMPONENTS
// ============================================

const FieldGroup = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    {children}
  </div>
);

const TextInput = ({ label, value, onChange, placeholder }) => (
  <FieldGroup label={label}>
    <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" />
  </FieldGroup>
);

const TextArea = ({ label, value, onChange, rows = 3, placeholder }) => (
  <FieldGroup label={label}>
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y" />
  </FieldGroup>
);

const SectionDivider = ({ title }) => (
  <div className="border-t border-slate-200 pt-6 mt-6 mb-4">
    <h3 className="text-lg font-bold text-slate-800">{title}</h3>
  </div>
);

// Reusable Image Upload Card with Preview (WebP, PNG only, 300x225 recommendation)
const ImageUploadCard = ({
  label,
  value,
  onChange,
  onRemove,
  canRemove = false,
  recommendedSize = '300 × 225 pixels',
  allowedFormats = 'WebP, PNG only',
  maxSizeLabel = 'WebP or PNG, max 5MB'
}) => {
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState(false);

  const previewSrc = value
    ? (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('blob:') || value.startsWith('data:')
        ? value
        : (value.startsWith('/') ? value : `/${value}`))
    : '';

  useEffect(() => {
    setImgError(false);
  }, [value]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate format: WebP or PNG only
    const ext = file.name.split('.').pop()?.toLowerCase();
    const isAllowedExt = ext === 'webp' || ext === 'png';
    const isAllowedMime = file.type === 'image/webp' || file.type === 'image/png';

    if (!isAllowedExt && !isAllowedMime) {
      toast.error('Only WebP and PNG formats are allowed');
      e.target.value = '';
      return;
    }

    // Validate size: max 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      e.target.value = '';
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post('/api/gmb-branches/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        toast.success('Image uploaded successfully');
        onChange(res.data.imageUrl || res.data.url);
      } else {
        toast.error(res.data?.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="space-y-1">
          <h4 className="text-base font-bold text-slate-800">{label}</h4>
          <div className="flex items-center gap-2 text-slate-500">
            <Triangle className="h-3.5 w-3.5 fill-slate-300 text-slate-400 rotate-90 flex-shrink-0" />
            <p className="text-xs sm:text-sm">
              Recommended: {recommendedSize} | Formats: {allowedFormats}
            </p>
          </div>
        </div>
        {canRemove && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors font-medium"
            title="Remove Image"
          >
            <TrashIcon className="w-4 h-4" />
            <span>Remove</span>
          </button>
        )}
      </div>

      {/* Upload area and Preview */}
      <div className="flex flex-col md:flex-row gap-6 items-start pt-1">
        {/* Left: Upload Box */}
        <div className="flex-1 w-full space-y-2">
          <label className={`block border-2 border-dashed ${uploading ? 'border-indigo-400 bg-indigo-50/20' : 'border-slate-300 hover:border-indigo-500'} rounded-2xl p-7 text-center cursor-pointer transition-colors group`}>
            <input
              type="file"
              accept=".webp,.png,image/webp,image/png"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />
            {uploading ? (
              <div className="flex flex-col items-center justify-center py-2">
                <Loader2 className="h-10 w-10 text-indigo-600 animate-spin mb-2" />
                <p className="text-base font-semibold text-indigo-600">Uploading Image...</p>
                <p className="text-xs text-slate-400 mt-1">Please wait</p>
              </div>
            ) : (
              <>
                <CloudUpload className="h-10 w-10 text-slate-300 group-hover:text-indigo-500 transition-colors mx-auto mb-2" />
                <p className="text-lg font-semibold text-slate-600 group-hover:text-indigo-600 transition-colors">
                  {value ? 'Change Image' : 'Upload Image'}
                </p>
                <p className="text-xs text-slate-400 mt-1">{maxSizeLabel}</p>
              </>
            )}
          </label>

          {/* Manual URL input fallback */}
          <div className="flex items-center gap-2 pt-1">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Or paste image URL (https://... or /img/...)"
              className="flex-1 px-3 py-1.5 text-xs text-slate-600 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="text-xs text-slate-400 hover:text-red-500 px-2 py-1 transition-colors"
                title="Clear image"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Right: Preview Box */}
        <div className="w-full md:w-72 space-y-2 flex-shrink-0">
          <p className="text-sm font-semibold text-slate-700">Preview</p>
          <div className="w-full aspect-[300/225] rounded-xl overflow-hidden border border-slate-200 shadow-sm relative group bg-slate-50 flex items-center justify-center">
            {previewSrc && !imgError ? (
              <>
                <img
                  src={previewSrc}
                  alt={label || 'Preview'}
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ImageIcon className="text-white h-8 w-8" />
                </div>
              </>
            ) : imgError ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-2 text-center">
                <ImageIcon className="text-slate-300 h-10 w-10 mb-1" />
                <span className="text-xs text-slate-500">Failed to load preview</span>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                <ImageIcon className="text-slate-200 h-12 w-12" />
                <span className="text-xs text-slate-400 mt-1">No Image</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SECTION FORM IMPLEMENTATIONS
// ============================================

const HeroForm = ({ data, update, add, remove }) => {
  const images = Array.isArray(data.images) ? data.images : [];
  const [activeLinkField, setActiveLinkField] = useState(null);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = (field) => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data[field] || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update(field, updatedValue);
    toast.success(`Inserted link: ${label}`);
    setActiveLinkField(null);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput label="Branch Label" value={data.branch_label} onChange={v => update('branch_label', v)} />
        <TextInput label="Breadcrumb Title" value={data.breadcrumb_title} onChange={v => update('breadcrumb_title', v)} placeholder="e.g. Affordable Home Loan Madurai" />
        <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />
        <TextInput label="Heading Line 2" value={data.heading_line2} onChange={v => update('heading_line2', v)} />
        <TextInput label="Heading Highlight" value={data.heading_highlight} onChange={v => update('heading_highlight', v)} />
      </div>

      {/* Description 1 with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setActiveLinkField(activeLinkField === 'description' ? null : 'description');
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{activeLinkField === 'description' ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {activeLinkField === 'description' && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setActiveLinkField(null)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleInsertLink('description')}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Description 2 with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description 2</label>
          <button
            type="button"
            onClick={() => {
              setActiveLinkField(activeLinkField === 'description2' ? null : 'description2');
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{activeLinkField === 'description2' ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {activeLinkField === 'description2' && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description 2</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setActiveLinkField(null)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleInsertLink('description2')}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description2 || ''}
          onChange={e => update('description2', e.target.value)}
          rows={2}
          placeholder="Enter second paragraph or highlight text with optional anchor tags."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: HTML anchor tags and line breaks are fully supported.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {(data.description || data.description2) && (
        <div className="bg-[#283079] p-5 rounded-2xl border border-indigo-900/30 text-white space-y-3 shadow-md">
          <div className="flex items-center justify-between border-b border-white/15 pb-2.5">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">Hero Description Live Preview</span>
            </div>
            <span className="text-xs text-gray-300">Live preview with clickable links</span>
          </div>
          {data.description && (
            <div
              className="gmb-hero-description text-gray-300 text-sm leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold"
              dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
            />
          )}
          {data.description2 && (
            <div
              className="gmb-hero-description text-gray-300 text-sm leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold pt-1 border-t border-white/10"
              dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description2) }}
            />
          )}
        </div>
      )}

      <SectionDivider title="Hero Buttons & Navigation Links" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50/80 p-4 rounded-xl space-y-3 border border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Button 1</span>
          </div>
          <TextInput
            label="Button 1 Text"
            value={data.button1_text !== undefined ? data.button1_text : 'Check Your Eligibility'}
            onChange={v => update('button1_text', v)}
            placeholder="e.g. Check Your Eligibility"
          />
          <TextInput
            label="Button 1 Navigation Link"
            value={data.button1_link || ''}
            onChange={v => update('button1_link', v)}
            placeholder="e.g. /eligibility or #enquiry (or blank for scroll)"
          />
        </div>

        <div className="bg-slate-50/80 p-4 rounded-xl space-y-3 border border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Button 2</span>
          </div>
          <TextInput
            label="Button 2 Text"
            value={data.button2_text !== undefined ? data.button2_text : 'Talk To A Loan Expert'}
            onChange={v => update('button2_text', v)}
            placeholder="e.g. Talk To A Loan Expert"
          />
          <TextInput
            label="Button 2 Navigation Link"
            value={data.button2_link || ''}
            onChange={v => update('button2_link', v)}
            placeholder="e.g. tel:+917823911555 or /contact"
          />
        </div>

        <div className="bg-slate-50/80 p-4 rounded-xl space-y-3 border border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Button 3</span>
          </div>
          <TextInput
            label="Button 3 Text"
            value={data.button3_text !== undefined ? data.button3_text : 'Customers Voice'}
            onChange={v => update('button3_text', v)}
            placeholder="e.g. Customers Voice"
          />
          <TextInput
            label="Button 3 Navigation Link"
            value={data.button3_link !== undefined ? data.button3_link : '#youtube-videos-section'}
            onChange={v => update('button3_link', v)}
            placeholder="e.g. #youtube-videos-section or /testimonials"
          />
        </div>
      </div>

      <SectionDivider title="Review Info" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextInput label="Review Rating" value={data.review_rating} onChange={v => update('review_rating', v)} />
        <TextInput label="Review Count" value={data.review_count} onChange={v => update('review_count', v)} />
        <TextInput label="Review Label" value={data.review_label} onChange={v => update('review_label', v)} />
      </div>

      <SectionDivider title="Features Checkmarks" />
      {data.features && data.features.map((f, i) => (
        <TextInput key={i} label={`Feature ${i + 1}`} value={f} onChange={v => update(`features.${i}`, v)} />
      ))}

      <SectionDivider title="Quick Enquiry Form" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput label="Form Title" value={data.form_title} onChange={v => update('form_title', v)} />
        <TextInput label="Form City Default" value={data.form_city_default} onChange={v => update('form_city_default', v)} />
      </div>
      <TextArea label="Form Subtitle" value={data.form_subtitle} onChange={v => update('form_subtitle', v)} rows={2} />

      <SectionDivider title="Social Links" />
      {data.social_links && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Facebook" value={data.social_links.facebook} onChange={v => update('social_links.facebook', v)} />
          <TextInput label="Instagram" value={data.social_links.instagram} onChange={v => update('social_links.instagram', v)} />
          <TextInput label="LinkedIn" value={data.social_links.linkedin} onChange={v => update('social_links.linkedin', v)} />
          <TextInput label="YouTube" value={data.social_links.youtube} onChange={v => update('social_links.youtube', v)} />
        </div>
      )}

      <SectionDivider title="Hero Images" />
      <div className="space-y-4">
        {images.map((img, i) => (
          <ImageUploadCard
            key={i}
            label={`Image ${i + 1} (Optional)`}
            value={img}
            onChange={v => update(`images.${i}`, v)}
            onRemove={remove ? () => remove('images', i) : undefined}
            canRemove={images.length > 1}
            recommendedSize="300 × 225 pixels"
            allowedFormats="WebP, PNG only"
            maxSizeLabel="WebP or PNG, max 5MB"
          />
        ))}

        {add && (
          <button
            type="button"
            onClick={() => add('images', '')}
            className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-semibold pt-1"
          >
            <PlusIcon className="w-4 h-4" /> Add Hero Image
          </button>
        )}
      </div>
    </div>
  );
};

const FeaturesStripForm = ({ data, update, add, remove, move }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-bold text-slate-800 mb-4">Feature Items</h3>
    {data.features && data.features.map((f, i) => (
      <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl">
        <div className="flex flex-col gap-1">
          <button onClick={() => move('features', i, -1)} disabled={i === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUpIcon className="w-3 h-3" /></button>
          <button onClick={() => move('features', i, 1)} disabled={i === data.features.length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDownIcon className="w-3 h-3" /></button>
        </div>
        <input type="text" value={f.label || ''} onChange={e => update(`features.${i}.label`, e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Feature label" />
        <input type="text" value={f.icon || ''} onChange={e => update(`features.${i}.icon`, e.target.value)} className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Icon key" />
        <button onClick={() => remove('features', i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4" /></button>
      </div>
    ))}
    <button onClick={() => add('features', { label: '', icon: '' })} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
      <PlusIcon className="w-4 h-4" /> Add Feature
    </button>
  </div>
);

const YouTubeForm = ({ data, update, add, remove, move }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />

      {/* Description with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setShowLinkModal(!showLinkModal);
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {showLinkModal && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {data.description && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Latest Videos Description Live Preview</span>
            </div>
            <span className="text-xs text-slate-400">Preview with clickable links</span>
          </div>
          <div
            className="gmb-youtube-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-red-600 [&_a]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
          />
        </div>
      )}

      <TextInput label="YouTube Channel URL" value={data.youtube_channel_url} onChange={v => update('youtube_channel_url', v)} />
    <SectionDivider title="Videos" />
    <div className="space-y-4">
      {data.videos && data.videos.map((v, i) => (
        <VideoItemCard
          key={i}
          video={v}
          index={i}
          total={data.videos.length}
          update={update}
          move={move}
          remove={remove}
        />
      ))}
      <button
        type="button"
        onClick={() => add('videos', { title: '', video_url: '', img: '' })}
        className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-semibold py-2 px-3 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors border border-indigo-200"
      >
        <PlusIcon className="w-4 h-4" /> Add Video
      </button>
    </div>
    </div>
  );
};

// Dedicated Video Item Card Editor with Thumbnail Upload & YouTube Auto-fetch
const VideoItemCard = ({ video, index, total, update, move, remove }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = React.useRef(null);

  const isYtUrl = (url) => typeof url === 'string' && (url.includes('youtube.com') || url.includes('youtu.be'));
  const currentVideoUrl = video.video_url !== undefined
    ? video.video_url
    : (isYtUrl(video.img) ? video.img : '');

  const effectiveThumbnail = resolveVideoThumbnail(video);
  const ytVideoId = getYouTubeVideoId(currentVideoUrl || video.img);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      e.target.value = '';
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post('/api/gmb-branches/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        toast.success('Thumbnail image uploaded successfully');
        const uploadedUrl = res.data.imageUrl || res.data.url;
        update(`videos.${index}.img`, uploadedUrl);
      } else {
        toast.error(res.data?.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Error uploading thumbnail:', err);
      toast.error(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleUseYouTubeThumbnail = () => {
    if (!ytVideoId) {
      toast.error('Please enter a valid YouTube video URL first');
      return;
    }
    const ytThumb = `https://img.youtube.com/vi/${ytVideoId}/hqdefault.jpg`;
    update(`videos.${index}.img`, ytThumb);
    toast.success('YouTube thumbnail applied!');
  };

  const handleVideoUrlChange = (newUrl) => {
    update(`videos.${index}.video_url`, newUrl);
    const newId = getYouTubeVideoId(newUrl);
    if (newId && (!video.img || isYtUrl(video.img))) {
      update(`videos.${index}.img`, `https://img.youtube.com/vi/${newId}/hqdefault.jpg`);
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl space-y-4 shadow-sm hover:border-indigo-300 transition-all">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600 font-bold text-xs">
            {index + 1}
          </span>
          <span className="text-sm font-bold text-slate-800">Video {index + 1}</span>
          {video.title && (
            <span className="text-xs text-slate-500 font-medium truncate max-w-xs">
              — {video.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => move('videos', index, -1)}
            disabled={index === 0}
            className="p-1.5 hover:bg-slate-200 rounded-lg disabled:opacity-30 transition-colors"
            title="Move Up"
          >
            <ChevronUpIcon className="w-4 h-4 text-slate-600" />
          </button>
          <button
            type="button"
            onClick={() => move('videos', index, 1)}
            disabled={index === total - 1}
            className="p-1.5 hover:bg-slate-200 rounded-lg disabled:opacity-30 transition-colors"
            title="Move Down"
          >
            <ChevronDownIcon className="w-4 h-4 text-slate-600" />
          </button>
          <button
            type="button"
            onClick={() => remove('videos', index)}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-1"
            title="Remove Video"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Grid: Form Inputs + Live Preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
        {/* Left 2 Cols: Form Inputs */}
        <div className="md:col-span-2 space-y-3.5">
          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Video Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={video.title || ''}
              onChange={e => update(`videos.${index}.title`, e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="e.g. Customer Testimonial or Branch Walkthrough"
            />
          </div>

          {/* Video URL */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-slate-700">
                Particular Video URL (YouTube Shorts or Watch URL) <span className="text-red-500">*</span>
              </label>
              {currentVideoUrl && (
                <a
                  href={currentVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-indigo-600 hover:text-indigo-800 underline font-medium"
                >
                  Test Link ↗
                </a>
              )}
            </div>
            <input
              type="text"
              value={currentVideoUrl}
              onChange={e => handleVideoUrlChange(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="e.g. https://www.youtube.com/shorts/A3zpiMO9BGw or https://www.youtube.com/watch?v=..."
            />
            <p className="mt-1 text-[11px] text-slate-500 leading-tight">
              Paste the full YouTube link. When a visitor clicks this card on the branch page, it will open this exact video.
            </p>
          </div>

          {/* Thumbnail Image Options */}
          <div className="bg-white p-3.5 rounded-xl border border-slate-200 space-y-2.5">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-700">
                Thumbnail Image Option
              </label>
              <button
                type="button"
                onClick={() => setShowUrlInput(!showUrlInput)}
                className="text-[11px] text-indigo-600 hover:underline font-medium"
              >
                {showUrlInput ? 'Hide URL input' : 'Enter Image URL manually'}
              </button>
            </div>

            {/* Buttons: Upload Thumbnail & Auto-fetch YouTube */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                accept=".webp,.png,.jpg,.jpeg,image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <CloudUpload className="w-3.5 h-3.5" />
                    <span>Upload Thumbnail Image</span>
                  </>
                )}
              </button>

              {ytVideoId && (
                <button
                  type="button"
                  onClick={handleUseYouTubeThumbnail}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold transition-colors"
                  title="Extract & use standard YouTube thumbnail"
                >
                  <span>⚡ Use YouTube Thumbnail</span>
                </button>
              )}

              {video.img && (
                <button
                  type="button"
                  onClick={() => update(`videos.${index}.img`, '')}
                  className="text-xs text-slate-400 hover:text-red-500 px-2 py-1 transition-colors"
                >
                  Clear Thumbnail
                </button>
              )}
            </div>

            {/* Manual URL input */}
            {(showUrlInput || (video.img && !isYtUrl(video.img))) && (
              <div className="pt-1">
                <input
                  type="text"
                  value={video.img || ''}
                  onChange={e => update(`videos.${index}.img`, e.target.value)}
                  placeholder="Paste thumbnail image URL (e.g. /img/... or https://...)"
                  className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500 bg-white"
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Col: Card Live Preview */}
        <div className="flex flex-col items-center">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
            Card Live Preview
          </span>
          <div className="w-36 aspect-[9/16] bg-gray-900 rounded-xl overflow-hidden relative shadow-md">
            {effectiveThumbnail ? (
              <img
                src={effectiveThumbnail}
                alt={video.title || 'Video preview'}
                className="w-full h-full object-cover opacity-80"
                onError={(e) => {
                  if (ytVideoId && e.target.src !== `https://img.youtube.com/vi/${ytVideoId}/hqdefault.jpg`) {
                    e.target.src = `https://img.youtube.com/vi/${ytVideoId}/hqdefault.jpg`;
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-3 text-center bg-slate-800 text-slate-400">
                <ImageIcon className="w-6 h-6 mb-1 opacity-50" />
                <span className="text-[10px]">No Thumbnail</span>
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white shadow">
                <Triangle className="w-3.5 h-3.5 fill-white rotate-90 ml-0.5" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-2.5 bg-gradient-to-t from-black/95 via-black/50 to-transparent">
              <p className="text-[10px] font-bold text-white leading-tight line-clamp-2">
                {video.title || 'Video Title'}
              </p>
            </div>
          </div>
          <span className="text-[10px] text-slate-400 mt-1.5 text-center">
            {effectiveThumbnail ? 'Thumbnail active' : 'Will use YouTube default'}
          </span>
        </div>
      </div>
    </div>
  );
};

// Reusable HeroIcons Picker with Dropdown and Live Preview matching Home Page Product Modal
const HeroIconPicker = ({ value, onChange, label = 'Icon (HeroIcons v2 Outline) *' }) => {
  const normalizedValue = normalizeHeroIconName(value) || 'MapPinIcon';
  const selectedOption = HERO_ICON_OPTIONS.find(opt => opt.name === normalizedValue) || HERO_ICON_OPTIONS[0];
  const IconComponent = selectedOption ? selectedOption.icon : HERO_ICON_OPTIONS[0].icon;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        {label}
      </label>
      <div className="space-y-3">
        {/* Icon Dropdown */}
        <select
          value={normalizedValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-sm cursor-pointer"
        >
          {HERO_ICON_OPTIONS.map((option) => (
            <option key={option.name} value={option.name}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Icon Preview */}
        <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <span className="text-sm font-medium text-slate-600">Preview:</span>
          <IconComponent className="h-8 w-8 text-[#283079]" />
          <span className="text-sm text-slate-500 font-medium">
            {selectedOption ? selectedOption.label : 'Home'}
          </span>
        </div>
      </div>
    </div>
  );
};

const CardsForm = ({ data, update, add, remove, move, cardFields, arrayKey }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />

      {/* Description with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setShowLinkModal(!showLinkModal);
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {showLinkModal && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {data.description && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Section Description Live Preview</span>
            </div>
            <span className="text-xs text-slate-400">Preview with clickable links</span>
          </div>
          <div
            className="gmb-whychoose-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
          />
        </div>
      )}

      <SectionDivider title="Cards" />
      {data[arrayKey] && data[arrayKey].map((card, i) => (
        <div key={i} className="bg-slate-50 p-4 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-600">Card {i + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => move(arrayKey, i, -1)} disabled={i === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
              <button onClick={() => move(arrayKey, i, 1)} disabled={i === data[arrayKey].length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
              <button onClick={() => remove(arrayKey, i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><TrashIcon className="w-4 h-4" /></button>
            </div>
          </div>
          {cardFields.map(field => (
            <div key={field}>
              {field === 'icon' ? (
                <HeroIconPicker
                  value={card.icon}
                  onChange={v => update(`${arrayKey}.${i}.icon`, v)}
                />
              ) : field === 'description' ? (
                <>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 capitalize">{field}</label>
                  <textarea value={card[field] || ''} onChange={e => update(`${arrayKey}.${i}.${field}`, e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </>
              ) : (
                <>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 capitalize">{field}</label>
                  <input type="text" value={card[field] || ''} onChange={e => update(`${arrayKey}.${i}.${field}`, e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </>
              )}
            </div>
          ))}
        </div>
      ))}
      <button onClick={() => add(arrayKey, cardFields.reduce((acc, f) => ({ ...acc, [f]: f === 'icon' ? 'MapPinIcon' : '' }), {}))} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
        <PlusIcon className="w-4 h-4" /> Add Card
      </button>
    </div>
  );
};

const LoanProductsForm = ({ data, update, add, remove, move }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />

      {/* Description with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setShowLinkModal(!showLinkModal);
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {showLinkModal && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {data.description && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Loan Products Description Live Preview</span>
            </div>
            <span className="text-xs text-slate-400">Preview with clickable links</span>
          </div>
          <div
            className="gmb-loanproducts-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
          />
        </div>
      )}

      <SectionDivider title="Products" />
      {data.products && data.products.map((p, i) => (
        <div key={i} className="bg-slate-50 p-4 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-600">Product {i + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => move('products', i, -1)} disabled={i === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
              <button onClick={() => move('products', i, 1)} disabled={i === data.products.length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
              <button onClick={() => remove('products', i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><TrashIcon className="w-4 h-4" /></button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Product Title</label>
            <input type="text" value={p.title || ''} onChange={e => update(`products.${i}.title`, e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Product title" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
            <textarea value={p.description || ''} onChange={e => update(`products.${i}.description`, e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Button Name</label>
              <input
                type="text"
                value={p.button_text !== undefined ? p.button_text : ''}
                onChange={e => update(`products.${i}.button_text`, e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Button text (Default: Learn More)"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Link URL</label>
              <input
                type="text"
                value={p.link || ''}
                onChange={e => update(`products.${i}.link`, e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. /home-construction-loan-india"
              />
            </div>
          </div>
          <HeroIconPicker
            value={p.icon}
            onChange={v => update(`products.${i}.icon`, v)}
            label="Product Icon (HeroIcons v2 Outline)"
          />
        </div>
      ))}
      <button onClick={() => add('products', { title: '', description: '', link: '', button_text: 'Learn More', icon: 'BuildingOffice2Icon' })} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
        <PlusIcon className="w-4 h-4" /> Add Product
      </button>
    </div>
  );
};

const StepsForm = ({ data, update, add, remove, move }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />

      {/* Description with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setShowLinkModal(!showLinkModal);
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {showLinkModal && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {data.description && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">The Process Description Live Preview</span>
            </div>
            <span className="text-xs text-slate-400">Preview with clickable links</span>
          </div>
          <div
            className="gmb-howtoapply-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
          />
        </div>
      )}

      <SectionDivider title="Steps" />
      {data.steps && data.steps.map((s, i) => (
        <div key={i} className="bg-slate-50 p-4 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-600">Step {i + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => move('steps', i, -1)} disabled={i === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
              <button onClick={() => move('steps', i, 1)} disabled={i === data.steps.length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
              <button onClick={() => remove('steps', i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><TrashIcon className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <input type="number" value={s.number || ''} onChange={e => update(`steps.${i}.number`, parseInt(e.target.value))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="#" />
            <input type="text" value={s.title || ''} onChange={e => update(`steps.${i}.title`, e.target.value)} className="col-span-3 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Step title" />
          </div>
          <textarea value={s.description || ''} onChange={e => update(`steps.${i}.description`, e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Step description" />
        </div>
      ))}
      <button onClick={() => add('steps', { number: (data.steps?.length || 0) + 1, title: '', description: '' })} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
        <PlusIcon className="w-4 h-4" /> Add Step
      </button>
    </div>
  );
};

const ReviewsForm = ({ data, update, add, remove, move }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />

      {/* Description with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setShowLinkModal(!showLinkModal);
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {showLinkModal && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {data.description && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Customer Stories Description Live Preview</span>
            </div>
            <span className="text-xs text-slate-400">Preview with clickable links</span>
          </div>
          <div
            className="gmb-customerstories-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
          />
        </div>
      )}

      <SectionDivider title="Reviews" />
      {data.reviews && data.reviews.map((r, i) => (
        <div key={i} className="bg-slate-50 p-4 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-600">Review {i + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => move('reviews', i, -1)} disabled={i === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
              <button onClick={() => move('reviews', i, 1)} disabled={i === data.reviews.length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
              <button onClick={() => remove('reviews', i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><TrashIcon className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input type="text" value={r.initials || ''} onChange={e => update(`reviews.${i}.initials`, e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Initials" />
            <input type="text" value={r.name || ''} onChange={e => update(`reviews.${i}.name`, e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Name" />
            <input type="number" value={r.rating || 5} onChange={e => update(`reviews.${i}.rating`, parseInt(e.target.value))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Rating" min={1} max={5} />
          </div>
          <textarea value={r.text || ''} onChange={e => update(`reviews.${i}.text`, e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Review text" />
        </div>
      ))}
      <button onClick={() => add('reviews', { initials: '', name: '', rating: 5, text: '' })} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
        <PlusIcon className="w-4 h-4" /> Add Review
      </button>
    </div>
  );
};

const VisitUsForm = ({ data, update }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />

      {/* Description with Link Inserter */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-slate-700">Description</label>
          <button
            type="button"
            onClick={() => {
              setShowLinkModal(!showLinkModal);
              setLinkText('');
              setLinkUrl('');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
          </button>
        </div>

        {showLinkModal && (
          <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
              <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                  placeholder="e.g. Ahamhfc or Contact Us"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="e.g. /contact"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
              >
                Insert &lt;a&gt; Tag
              </button>
            </div>
          </div>
        )}

        <textarea
          value={data.description || ''}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="Enter description text. HTML tags like <a href='/contact'>Ahamhfc</a> are supported."
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
        </p>
      </div>

      {/* Live Preview of Description with Anchor Links */}
      {data.description && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Visit Us Description Live Preview</span>
            </div>
            <span className="text-xs text-slate-400">Preview with clickable links</span>
          </div>
          <div
            className="gmb-visitus-description text-gray-600 text-sm leading-relaxed [&_a]:text-[#283079] [&_a]:underline hover:[&_a]:text-yellow-600 [&_a]:font-semibold"
            dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(data.description) }}
          />
        </div>
      )}

      <SectionDivider title="Branch Information" />
    <TextInput label="Branch Info Title" value={data.branch_info_title} onChange={v => update('branch_info_title', v)} />
    <TextArea label="Address" value={data.address} onChange={v => update('address', v)} rows={2} />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <TextInput label="City, State, PIN" value={data.city_state_pin} onChange={v => update('city_state_pin', v)} />
      <TextInput label="Phone" value={data.phone} onChange={v => update('phone', v)} />
      <TextInput label="Email" value={data.email} onChange={v => update('email', v)} />
      <TextInput label="Working Hours" value={data.working_hours} onChange={v => update('working_hours', v)} />
      <TextInput label="Landmark" value={data.landmark} onChange={v => update('landmark', v)} />
    </div>
    <SectionDivider title="Action Buttons" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-slate-50/80 p-4 rounded-xl space-y-3 border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Button 1 (Call)</span>
        </div>
        <TextInput
          label="Button 1 Text"
          value={data.button1_text !== undefined ? data.button1_text : 'Call Branch'}
          onChange={v => update('button1_text', v)}
          placeholder="e.g. Call Branch"
        />
        <TextInput
          label="Button 1 Navigation Link"
          value={data.button1_link || ''}
          onChange={v => update('button1_link', v)}
          placeholder="e.g. tel:+917823911555"
        />
      </div>

      <div className="bg-slate-50/80 p-4 rounded-xl space-y-3 border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Button 2 (Directions)</span>
        </div>
        <TextInput
          label="Button 2 Text"
          value={data.button2_text !== undefined ? data.button2_text : 'Get Directions'}
          onChange={v => update('button2_text', v)}
          placeholder="e.g. Get Directions"
        />
        <TextInput
          label="Button 2 Navigation Link (e.g. Directions Link)"
          value={data.button2_link || ''}
          onChange={v => update('button2_link', v)}
          placeholder="e.g. https://maps.app.goo.gl/FAQR1jtgfowF8Phf8"
        />
      </div>
    </div>

    <SectionDivider title="Google Maps" />
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          Map Embed URL (or paste &lt;iframe&gt; code)
        </label>
        <input
          type="text"
          value={data.map_embed_url || ''}
          onChange={(e) => {
            const raw = e.target.value;
            const clean = extractMapEmbedUrl(raw);
            if (raw.includes('<iframe') && clean !== raw) {
              toast.success('Extracted map embed URL from iframe tag!');
            }
            update('map_embed_url', clean);
          }}
          placeholder="e.g. https://www.google.com/maps/embed?pb=... or paste full <iframe> code"
          className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Tip: From Google Maps, click <strong>Share &gt; Embed a map &gt; Copy HTML</strong> and paste it here. The URL will be extracted automatically to accurately pin the branch!
        </p>
      </div>

      {data.map_embed_url && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Google Maps Live Preview
            </span>
            <span className="text-xs text-slate-400">Verifying branch pin location</span>
          </div>
          <div className="w-full h-64 rounded-xl overflow-hidden border border-slate-300 bg-white shadow-inner">
            <iframe
              title="Google Map Embed Preview"
              src={extractMapEmbedUrl(data.map_embed_url)}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

const GalleryForm = ({ data, update, add, remove }) => {
  const images = Array.isArray(data.images) ? data.images : [];

  return (
    <div className="space-y-4">
      <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
      <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />
      <SectionDivider title="Gallery Images" />
      <div className="space-y-4">
        {images.map((img, i) => (
          <ImageUploadCard
            key={i}
            label={`Gallery Image ${i + 1}`}
            value={img}
            onChange={v => update(`images.${i}`, v)}
            onRemove={remove ? () => remove('images', i) : undefined}
            canRemove={images.length > 1}
            recommendedSize="400 × 300 pixels"
            allowedFormats="WebP, PNG only"
            maxSizeLabel="WebP or PNG, max 5MB"
          />
        ))}
        {add && (
          <button
            type="button"
            onClick={() => add('images', '')}
            className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-semibold pt-1"
          >
            <PlusIcon className="w-4 h-4" /> Add Gallery Image
          </button>
        )}
      </div>
    </div>
  );
};

const CoverageForm = ({ data, update, add, remove, move }) => (
  <div className="space-y-4">
    <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
    <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />
    <TextInput label="Extra Label" value={data.extra_label} onChange={v => update('extra_label', v)} />

    <SectionDivider title="Coverage Areas" />
    {data.areas && data.areas.map((area, i) => (
      <div key={i} className="flex items-center gap-3">
        <input type="text" value={area || ''} onChange={e => update(`areas.${i}`, e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        <button onClick={() => remove('areas', i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4" /></button>
      </div>
    ))}
    <button onClick={() => add('areas', '')} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
      <PlusIcon className="w-4 h-4" /> Add Area
    </button>

    <SectionDivider title="Nearby Branches" />
    <TextInput label="Nearby Branches Heading" value={data.nearby_branches_heading} onChange={v => update('nearby_branches_heading', v)} />
    {data.nearby_branches && data.nearby_branches.map((b, i) => (
      <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl">
        <input type="text" value={b.label || ''} onChange={e => update(`nearby_branches.${i}.label`, e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Label" />
        <input type="text" value={b.link || ''} onChange={e => update(`nearby_branches.${i}.link`, e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Link" />
        <button onClick={() => remove('nearby_branches', i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4" /></button>
      </div>
    ))}
    <button onClick={() => add('nearby_branches', { label: '', link: '' })} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
      <PlusIcon className="w-4 h-4" /> Add Nearby Branch
    </button>
  </div>
);

const FAQForm = ({ data, update, add, remove, move }) => (
  <div className="space-y-4">
    <TextInput label="Section Tag" value={data.section_tag} onChange={v => update('section_tag', v)} />
    <TextInput label="Heading" value={data.heading} onChange={v => update('heading', v)} />
    <SectionDivider title="FAQ Items" />
    {data.faqs && data.faqs.map((f, i) => (
      <div key={i} className="bg-slate-50 p-4 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-slate-600">FAQ {i + 1}</span>
          <div className="flex gap-1">
            <button onClick={() => move('faqs', i, -1)} disabled={i === 0} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
            <button onClick={() => move('faqs', i, 1)} disabled={i === data.faqs.length - 1} className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
            <button onClick={() => remove('faqs', i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><TrashIcon className="w-4 h-4" /></button>
          </div>
        </div>
        <input type="text" value={f.question || ''} onChange={e => update(`faqs.${i}.question`, e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Question" />
        <textarea value={f.answer || ''} onChange={e => update(`faqs.${i}.answer`, e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Answer" />
      </div>
    ))}
    <button onClick={() => add('faqs', { question: '', answer: '' })} className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
      <PlusIcon className="w-4 h-4" /> Add FAQ
    </button>
  </div>
);

const CTAForm = ({ data, update }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Please enter a valid URL (e.g. /contact)');
      return;
    }
    const label = linkText.trim() || 'Link';
    const cleanUrl = linkUrl.trim();
    const tag = `<a href="${cleanUrl}">${label}</a>`;
    const currentValue = data.description || '';
    const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
    update('description', updatedValue);
    toast.success(`Inserted link: ${label}`);
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs text-slate-500 font-medium leading-relaxed">
          This section controls the final Call-to-Action banner displayed at the bottom of the branch page, including the caption, heading, description, and action buttons.
        </p>
      </div>

      {/* Section Content */}
      <div className="space-y-4">
        <TextInput
          label="Section Tag / Caption (Above Heading)"
          value={data.section_tag !== undefined ? data.section_tag : 'GET STARTED'}
          onChange={v => update('section_tag', v)}
          placeholder="e.g. GET STARTED"
        />
        <TextInput
          label="Heading"
          value={data.heading !== undefined ? data.heading : 'Ready To Explore Your Home Loan Options?'}
          onChange={v => update('heading', v)}
          placeholder="e.g. Ready To Explore Your Home Loan Options?"
        />

        {/* Description with Link Inserter */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-semibold text-slate-700">Description</label>
            <button
              type="button"
              onClick={() => {
                setShowLinkModal(!showLinkModal);
                setLinkText('');
                setLinkUrl('');
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors border border-indigo-200"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
            </button>
          </div>

          {showLinkModal && (
            <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-indigo-900">Insert Link into Description</span>
                <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text (Visible words)</label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={e => setLinkText(e.target.value)}
                    placeholder="e.g. Ahamhfc or Contact Us"
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                  <input
                    type="text"
                    value={linkUrl}
                    onChange={e => setLinkUrl(e.target.value)}
                    placeholder="e.g. /contact"
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleInsertLink}
                  className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
                >
                  Insert &lt;a&gt; Tag
                </button>
              </div>
            </div>
          )}

          <textarea
            value={data.description !== undefined ? data.description : ''}
            onChange={e => update('description', e.target.value)}
            rows={3}
            placeholder="e.g. Speak with our team to understand eligibility, documents required, and available loan options."
            className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-y font-normal"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Tip: You can include clickable links using HTML anchor tags, e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono text-[11px]">&lt;a href="/contact"&gt;Ahamhfc&lt;/a&gt;</code>.
          </p>
        </div>
      </div>

      {/* Button 1 */}
      <SectionDivider title="Button 1 (Left Action)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl">
        <TextInput
          label="Button 1 Text"
          value={data.button1_text !== undefined ? data.button1_text : 'Check Eligibility Now'}
          onChange={v => update('button1_text', v)}
          placeholder="e.g. Check Eligibility Now"
        />
        <TextInput
          label="Button 1 Link (URL / Route or blank to scroll up)"
          value={data.button1_link || ''}
          onChange={v => update('button1_link', v)}
          placeholder="e.g. /eligibility-calculator or blank for scroll to top"
        />
      </div>

      {/* Button 2 */}
      <SectionDivider title="Button 2 (Center Action)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl">
        <TextInput
          label="Button 2 Text"
          value={data.button2_text !== undefined ? data.button2_text : 'Request A Callback'}
          onChange={v => update('button2_text', v)}
          placeholder="e.g. Request A Callback"
        />
        <TextInput
          label="Button 2 Link (URL / Route or blank to scroll up)"
          value={data.button2_link || ''}
          onChange={v => update('button2_link', v)}
          placeholder="e.g. tel:+917823911555 or blank for enquiry scroll"
        />
      </div>

      {/* Button 3 */}
      <SectionDivider title="Button 3 (Right Action)" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl">
        <TextInput
          label="Button 3 Text"
          value={data.button3_text !== undefined ? data.button3_text : 'WhatsApp Our Team'}
          onChange={v => update('button3_text', v)}
          placeholder="e.g. WhatsApp Our Team"
        />
        <TextInput
          label="Button 3 Link (WhatsApp Link or Route)"
          value={data.button3_link !== undefined ? data.button3_link : 'https://wa.me/917823911555'}
          onChange={v => update('button3_link', v)}
          placeholder="e.g. https://wa.me/917823911555"
        />
      </div>

      {/* Live Preview */}
      <SectionDivider title="Live Preview (How it appears on page)" />
      <div className="bg-[#283079] rounded-2xl p-8 md:p-12 text-center text-white space-y-4 shadow-md">
        <div className="flex items-center space-x-3 justify-center">
          <div className="w-6 h-[2px] bg-yellow-500"></div>
          <span className="text-yellow-500 font-semibold tracking-wider text-xs md:text-sm uppercase">
            {data.section_tag || 'GET STARTED'}
          </span>
          <div className="w-6 h-[2px] bg-yellow-500"></div>
        </div>
        <h3 className="text-xl md:text-2xl font-bold font-serif text-white">
          {data.heading || 'Ready To Explore Your Home Loan Options?'}
        </h3>
        <div
          className="gmb-cta-description text-gray-300 text-xs md:text-sm max-w-xl mx-auto leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold"
          dangerouslySetInnerHTML={{
            __html: sanitizeAndFormatHtml(
              data.description || 'Speak with our team to understand eligibility, documents required, and available loan options.'
            ),
          }}
        />
        <div className="flex flex-wrap justify-center gap-3 pt-3">
          <span className="border border-gray-400 text-white text-xs font-semibold py-2 px-5 rounded-full">
            {data.button1_text || 'Check Eligibility Now'}
          </span>
          <span className="border border-gray-400 text-white text-xs font-semibold py-2 px-5 rounded-full">
            {data.button2_text || 'Request A Callback'}
          </span>
          <span className="border border-gray-400 text-white text-xs font-semibold py-2 px-5 rounded-full">
            {data.button3_text || 'WhatsApp Our Team'}
          </span>
        </div>
      </div>
    </div>
  );
};

const SEOForm = ({ data, update, branchInfo }) => {
  const getInitialSchemaText = useCallback(() => {
    if (!data.schema) return '';
    if (typeof data.schema === 'string') return data.schema;
    try {
      return JSON.stringify(data.schema, null, 2);
    } catch (e) {
      return String(data.schema);
    }
  }, [data.schema]);

  const [schemaText, setSchemaText] = useState(getInitialSchemaText);

  // Synchronize when external schema changes (e.g. initial load or refetch)
  useEffect(() => {
    const text = getInitialSchemaText();
    setSchemaText(prev => {
      if (!prev && text) return text;
      return prev;
    });
  }, [getInitialSchemaText]);

  const validation = useMemo(() => {
    const trimmed = (schemaText || '').trim();
    if (!trimmed) {
      return { status: 'empty', message: 'No custom schema markup added (optional).' };
    }
    const parsed = cleanAndNormalizeSchema(trimmed);
    if (parsed && typeof parsed === 'object') {
      const types = [];
      if (parsed['@type']) types.push(parsed['@type']);
      if (Array.isArray(parsed['@graph'])) {
        parsed['@graph'].forEach(item => {
          if (item && item['@type'] && !types.includes(item['@type'])) {
            types.push(item['@type']);
          }
        });
      }
      return {
        status: 'valid',
        message: 'Valid JSON-LD Schema',
        types,
        context: parsed['@context'] || 'https://schema.org',
        graphCount: Array.isArray(parsed['@graph']) ? parsed['@graph'].length : null,
        parsed
      };
    }
    return {
      status: 'error',
      message: 'Invalid syntax. Ensure braces/brackets are matched or JSON syntax is valid.'
    };
  }, [schemaText]);

  const handleTextChange = (e) => {
    const newVal = e.target.value;
    setSchemaText(newVal);
    const parsed = cleanAndNormalizeSchema(newVal);
    if (parsed && typeof parsed === 'object') {
      update('schema', parsed);
    } else {
      update('schema', newVal);
    }
  };

  const handleFormat = () => {
    if (validation.status === 'valid' && validation.parsed) {
      const formatted = JSON.stringify(validation.parsed, null, 2);
      setSchemaText(formatted);
      update('schema', validation.parsed);
      toast.success('Schema formatted and beautified successfully!');
    } else {
      toast.error('Cannot format invalid schema. Please check syntax.');
    }
  };

  const handleLoadSample = () => {
    const branchName = branchInfo?.branch_name || 'Madurai';
    const canonicalUrl = data.canonical || `https://ahamhfc.com/home-loan-in-${branchName.toLowerCase().replace(/\s+/g, '-')}`;
    const sample = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://ahamhfc.com/#organization",
          "name": "AHAM HOUSING FINANCE LIMITED",
          "legalName": "AHAM HOUSING FINANCE LIMITED",
          "alternateName": "Aham Housing Finance Private Limited",
          "url": "https://ahamhfc.com/",
          "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
          "description": "AHAM HOUSING FINANCE LIMITED is an affordable housing finance company helping customers access home loans across multiple regions in India.",
          "telephone": "+91 78239 11555",
          "email": "info@ahamhfc.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
            "addressLocality": "Chennai",
            "addressRegion": "Tamil Nadu",
            "postalCode": "600034",
            "addressCountry": "IN"
          },
          "sameAs": [
            "https://www.facebook.com/ahamhfc",
            "https://www.instagram.com/aham_housingfinance/",
            "https://linkedin.com/company/ahamhfc",
            "https://www.youtube.com/@ahamhfc"
          ]
        },
        {
          "@type": "FinancialService",
          "@id": `${canonicalUrl}#financialservice`,
          "name": `AHAM HOUSING FINANCE LIMITED - ${branchName} Branch`,
          "url": canonicalUrl,
          "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
          "description": `AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions in ${branchName}.`,
          "serviceType": [
            "Home Loans",
            "Affordable Housing Finance"
          ],
          "areaServed": {
            "@type": "City",
            "name": branchName
          },
          "telephone": "+91 78239 11555",
          "email": "info@ahamhfc.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
            "addressLocality": "Chennai",
            "addressRegion": "Tamil Nadu",
            "postalCode": "600034",
            "addressCountry": "IN"
          },
          "provider": {
            "@id": "https://ahamhfc.com/#organization"
          }
        },
        {
          "@type": "WebSite",
          "@id": "https://ahamhfc.com/#website",
          "url": "https://ahamhfc.com/",
          "name": "AHAM HOUSING FINANCE LIMITED",
          "publisher": {
            "@id": "https://ahamhfc.com/#organization"
          },
          "inLanguage": "en-IN"
        },
        {
          "@type": "WebPage",
          "@id": `${canonicalUrl}#webpage`,
          "url": canonicalUrl,
          "name": data.title || `Affordable Home Loan in ${branchName} | Aham Housing Finance`,
          "description": data.description || `Find Aham Housing Finance ${branchName} branch for affordable home loans and customer assistance.`,
          "isPartOf": {
            "@id": "https://ahamhfc.com/#website"
          },
          "about": {
            "@id": "https://ahamhfc.com/#organization"
          },
          "publisher": {
            "@id": "https://ahamhfc.com/#organization"
          },
          "primaryImageOfPage": {
            "@type": "ImageObject",
            "contentUrl": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${canonicalUrl}#breadcrumb`,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://ahamhfc.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": `Affordable Home Loan ${branchName}`,
              "item": canonicalUrl
            }
          ]
        }
      ]
    };
    const formatted = JSON.stringify(sample, null, 2);
    setSchemaText(formatted);
    update('schema', sample);
    toast.success('Sample branch schema loaded!');
  };

  const handleClear = () => {
    setSchemaText('');
    update('schema', null);
    toast.success('Schema cleared');
  };

  return (
    <div className="space-y-4">
      <TextInput
        label="Page Title"
        value={data.title}
        onChange={v => update('title', v)}
        placeholder="e.g. Affordable Home Loan in Madurai | Aham Housing Finance"
      />
      <TextArea
        label="Meta Description"
        value={data.description}
        onChange={v => update('description', v)}
        rows={3}
        placeholder="e.g. Looking for an affordable home loan in Madurai? Aham Housing Finance helps families..."
      />
      <TextInput
        label="Keywords"
        value={data.keywords}
        onChange={v => update('keywords', v)}
        placeholder="e.g. home loan in Madurai, affordable home loan Madurai"
      />
      <TextInput
        label="Canonical URL"
        value={data.canonical}
        onChange={v => update('canonical', v)}
        placeholder="https://ahamhfc.com/home-loan-in-madurai"
      />

      <SectionDivider title="Open Graph" />
      <TextInput
        label="OG Title"
        value={data.og_title}
        onChange={v => update('og_title', v)}
        placeholder="OG Title (defaults to Page Title if blank)"
      />
      <TextArea
        label="OG Description"
        value={data.og_description}
        onChange={v => update('og_description', v)}
        rows={2}
        placeholder="OG Description"
      />

      <SectionDivider title="SEO Schema Markup (JSON-LD Structured Data)" />
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-slate-800">Custom Schema (JSON-LD)</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Paste your Schema JSON-LD below. Supports pure JSON, <code className="bg-white px-1 py-0.5 rounded border border-slate-200">&lt;script&gt;</code> tags, or JavaScript declarations (<code className="bg-white px-1 py-0.5 rounded border border-slate-200">const branchLocatorSchema = &#123;...&#125;</code>).
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleFormat}
              disabled={validation.status !== 'valid'}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-lg text-xs font-semibold shadow-sm transition-all flex items-center gap-1.5"
            >
              <span>Format & Prettify</span>
            </button>
            <button
              type="button"
              onClick={handleLoadSample}
              className="px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 rounded-lg text-xs font-medium transition-all"
            >
              Load Template
            </button>
            {schemaText && (
              <button
                type="button"
                onClick={handleClear}
                className="px-2.5 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-xs font-medium transition-all"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Validation Status Bar */}
        <div className={`p-3 rounded-xl border text-xs flex flex-wrap items-center justify-between gap-2 ${
          validation.status === 'valid'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : validation.status === 'error'
            ? 'bg-rose-50 border-rose-200 text-rose-800'
            : 'bg-slate-100 border-slate-200 text-slate-600'
        }`}>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${
              validation.status === 'valid'
                ? 'bg-emerald-500'
                : validation.status === 'error'
                ? 'bg-rose-500'
                : 'bg-slate-400'
            }`} />
            <span className="font-semibold">{validation.message}</span>
            {validation.graphCount !== null && (
              <span className="ml-1 text-[11px] opacity-80">({validation.graphCount} items in @graph)</span>
            )}
          </div>

          {validation.types && validation.types.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">Detected Types:</span>
              {validation.types.map((t, idx) => (
                <span key={idx} className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md text-[11px] font-medium border border-emerald-300">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Schema Code Textarea */}
        <div className="relative">
          <textarea
            value={schemaText}
            onChange={handleTextChange}
            rows={18}
            placeholder={`// Paste your Schema here, e.g.:
{
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "AHAM HOUSING FINANCE LIMITED"
    }
  ]
}`}
            className="w-full font-mono text-xs leading-relaxed border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors shadow-inner"
            spellCheck="false"
          />
        </div>

        <p className="text-[11px] text-slate-500 leading-normal">
          💡 <strong>How it works:</strong> The schema will be automatically converted to standard JSON-LD and injected into the branch page's <code className="bg-white px-1 py-0.5 rounded border border-slate-200">&lt;script type="application/ld+json"&gt;</code> tag for Google & search engine crawlers.
        </p>
      </div>
    </div>
  );
};

const JSONEditor = ({ data, update }) => {
  const [jsonStr, setJsonStr] = useState(JSON.stringify(data, null, 2));
  const [jsonError, setJsonError] = useState(null);

  const handleChange = (value) => {
    setJsonStr(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const handleApply = () => {
    try {
      const parsed = JSON.parse(jsonStr);
      Object.keys(parsed).forEach(key => {
        update(key, parsed[key]);
      });
    } catch (e) {
      // ignore
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">No specific editor for this section. Edit the raw JSON below:</p>
      <textarea
        value={jsonStr}
        onChange={e => handleChange(e.target.value)}
        rows={20}
        className={`w-full border ${jsonError ? 'border-red-300' : 'border-slate-300'} rounded-xl px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-indigo-500`}
      />
      {jsonError && <p className="text-red-500 text-xs">{jsonError}</p>}
      <button onClick={handleApply} disabled={!!jsonError} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">Apply JSON</button>
    </div>
  );
};

export default AdminGMBSectionEditor;
